import { TTicketMember } from '@/features/ticketing/tickets/types/ticket.type';
import React, { memo } from 'react';
import { Ban, Loader2, SendHorizonal, X } from 'lucide-react';
import Avatar from '@/components/ui/avatar';
import { format } from 'date-fns';
import { cn } from '@/utils/cn';
import EmptySection from '@/components/ui/_r/empty/empty-section';
import LoadingSpinner2 from '@/components/ui/new-loading-spinner';
import { TicketCommentPopover } from '../ticket-comment-popover';
import { TypographyWithMention } from '@/components/ui/_refactor/data-display/typography-with-mention';
import TextareaMention from '../textarea-mention';
import { useTicketComment } from './use-ticket-comment';

export interface ITicketCommentProps {
  ticketId: string;
  listTicketMember: TTicketMember[];
  isDisabled?: boolean;
}
const TicketComment = memo(({ ticketId, listTicketMember, isDisabled }: ITicketCommentProps) => {
  // HOOKS
  const {
    // State
    user, // Current authenticated user (for checking comment ownership)
    text, // Display text (what user sees in textarea)
    editedTicketComment, // Comment currently being edited (null if not editing)
    listTicketCommentMapped, // Sorted list of comments (oldest first)
    isLoadingListTicketCommentData, // Loading state for comments fetch

    // Refs
    containerRef, // Ref for comment input container
    commentsContainerRef, // Ref for scrollable comments list
    lastCommentRef, // Ref for last comment element (for scroll tracking)

    // Form
    form, // React Hook Form instance (for validation and form state)

    // Mutations
    mutationCreateTicketComment, // Mutation for creating new comments
    mutationUpdateTicketComment, // Mutation for updating existing comments

    // Handlers
    handleSubmitComment, // Submit comment (create or update)
    handleCancelEdit, // Cancel editing mode
    handleChangeComment, // Handle display text changes
    handleChangeFormatted, // Handle formatted text changes (with mention IDs)
  } = useTicketComment(ticketId);

  // RENDER
  if (isLoadingListTicketCommentData) {
    return (
      <div className="bg-[#F0F0F0] h-full flex-1 flex flex-col border-l border-[#D9D9D9]">
        <div className="flex items-center justify-center w-full h-full ">
          <LoadingSpinner2 className="w-[26px] h-[26px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F0F0F0] border-l border-[#D9D9D9]">
      {/* Activity Feed */}
      <div ref={commentsContainerRef} className="flex-1 p-4 space-y-4 overflow-y-auto">
        {listTicketCommentMapped.length === 0 ? (
          <EmptySection
            type="message"
            title="Comments will appear here"
            description="Make a comment to start the conversation."
          />
        ) : null}

        {listTicketCommentMapped.length > 0
          ? listTicketCommentMapped.map((comment, index) => {
              const isLastItem = Boolean(index === listTicketCommentMapped.length - 1);
              const isCurrentUser = Boolean(comment?.user?.userId === user?.userId) ?? false;
              const isDeletedComment = Boolean(comment?.isDeleted) ?? false;
              const isEditedComment = Boolean(comment?.editCount && comment?.editCount > 0) ?? false;
              const isDisplayTicketCommentPopover = Boolean(isCurrentUser && !isDeletedComment) ?? false;

              return (
                <div
                  key={index + 1}
                  ref={isLastItem ? lastCommentRef : null}
                  className={cn('flex items-start justify-between', { 'pb-[16px]': isLastItem })}
                >
                  <div className="flex items-start gap-[8px]">
                    <Avatar
                      imgSrc={comment?.user?.profileImage || ''}
                      username={comment?.user?.fullname || ''}
                      className="w-[22px] h-[22px] shrink-0"
                    />
                    <div className="flex flex-col gap-[4px] min-w-0">
                      <span className="text-[12px] font-[700] text-[#25151E]">{comment?.user?.fullname || ''}</span>
                      {isDeletedComment ? (
                        <div className="flex items-center gap-[2px]">
                          <Ban className="w-[12px] h-[12px] flex-shrink-0 text-[#25151E]" />
                          <span className="text-[12px] font-[400] text-[#25151E] italic">
                            This comment has been deleted.
                          </span>
                        </div>
                      ) : (
                        <p className="text-[12px] font-[400] text-[#25151E] overflow-x-hidden break-all whitespace-pre-wrap">
                          <TypographyWithMention
                            content={comment?.content || ''}
                            mentionClassName="font-bold text-[#25151E]"
                            textClassName="text-[#25151E]"
                            linkClassName="text-[#0C8DBF] underline hover:opacity-80 font-[700]"
                          />
                        </p>
                      )}
                      <div className="flex items-center gap-[4px]">
                        <span className="text-[8px] font-[400] text-[#9D9D9C]">
                          {comment?.createdAt ? format(comment?.createdAt, 'dd MMM yyyy, HH:mm') : '-'}
                        </span>
                        {isEditedComment ? (
                          <span className="text-[8px] font-[700] text-[#9D9D9C]">
                            {comment?.editCount ? `Edited (${comment?.editCount})` : ''}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {isDisplayTicketCommentPopover ? (
                    <TicketCommentPopover ticketComment={comment} ticketId={ticketId} />
                  ) : null}
                </div>
              );
            })
          : null}
      </div>

      {/* Comment Input */}
      {!isDisabled ? (
        <div className="border-t border-[#D9D9D9] p-[12px] bg-white" ref={containerRef}>
          {/* Preview of original comment when editing */}
          {editedTicketComment && (
            <div className="mb-[12px] p-[12px] bg-[#F5F5F5] rounded-lg border border-[#D9D9D9]">
              <div className="flex items-start justify-between gap-[8px] mb-[8px]">
                <div className="flex items-center gap-[8px]">
                  <Avatar
                    imgSrc={editedTicketComment?.user?.profileImage || ''}
                    username={editedTicketComment?.user?.fullname || ''}
                    className="w-[22px] h-[22px] shrink-0"
                  />
                  <span className="text-[12px] font-[700] text-[#25151E]">
                    {editedTicketComment?.user?.fullname || ''}
                  </span>
                </div>
                <button
                  onClick={handleCancelEdit}
                  className="p-[4px] hover:bg-[#D9D9D9] rounded transition-colors shrink-0"
                  type="button"
                >
                  <X className="w-[14px] h-[14px] text-[#25151E]" />
                </button>
              </div>
              <p className="text-[12px] font-[400] text-[#25151E] italic overflow-x-hidden break-all">
                Original:{' '}
                <TypographyWithMention
                  content={editedTicketComment.content || ''}
                  mentionClassName="font-bold text-[#25151E]"
                  textClassName="text-[#25151E]"
                  linkClassName="text-[#0C8DBF] underline hover:opacity-80 font-[700]"
                />
              </p>
            </div>
          )}
          <div className="flex items-center gap-[8px] ">
            <TextareaMention
              value={text}
              onChange={handleChangeComment}
              onChangeFormatted={handleChangeFormatted}
              mentions={listTicketMember}
              placeholder={editedTicketComment ? 'Edit your comment' : 'Add a comment'}
              className="w-full"
              hasError={Boolean(form.formState.errors.content)}
            />
            <button
              onClick={handleSubmitComment}
              disabled={
                mutationCreateTicketComment.isPending ||
                mutationUpdateTicketComment.isPending ||
                !text.trim() ||
                form.formState.isSubmitting
              }
              className={cn(
                'rounded-[8px] p-[8px] flex items-center justify-center transition-all duration-300 group shrink-0',
                {
                  'bg-[#0C8DBF] hover:bg-[#0C8DBF]/90': text.trim(),
                  'bg-[#D9D9D9] cursor-not-allowed hover:bg-[#D9D9D9]': !text.trim(),
                }
              )}
            >
              {mutationCreateTicketComment.isPending ||
              mutationUpdateTicketComment.isPending ||
              form.formState.isSubmitting ? (
                <Loader2 className="w-[20px] h-[20px] animate-spin text-white" />
              ) : (
                <SendHorizonal className="w-[20px] text-white h-[20px]" />
              )}
            </button>
          </div>
          {form.formState.errors.content && (
            <span className="text-[10px] text-red-500 font-[400]">{form.formState.errors.content.message}</span>
          )}
        </div>
      ) : null}
    </div>
  );
});
TicketComment.displayName = 'TicketComment';
export default TicketComment;
